import { FC } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import __ from "src/utils/Translations";
import useIsAhr from "src/utils/hooks/useIsAhr";
import { useCompanyId, useCompanyName } from "src/Pages/Company/CompanyContext";
import { getCompanyBaseBreadcrumbs } from "src/Pages/Company/routerHelper";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import { useFetchSyncErrorDetails } from "src/Pages/Company/OrganizationStructure/api/useFetchSyncErrorDetails";
import BackButton from "src/Pages/Company/OrganizationStructure/sharedComponents/BackButton";
import { StatusChip } from "src/Pages/Company/EmployeeManagement/SyncErrors/components/StatusChip";
import { SyncErrorDetail } from "./syncErrorDetails.models";
import { getSyncErrorMessage } from "./syncErrorMessages";

interface LocationState {
  firstName?: string | null;
  lastName?: string | null;
  fk?: string;
  issuedAt?: string;
}

const SyncErrorDetails: FC = () => {
  const companyId = useCompanyId();
  const companyName = useCompanyName();
  const isAhr = useIsAhr();
  const { employeeFk } = useParams<{ employeeFk: string }>();
  const location = useLocation<LocationState>();
  const { firstName, lastName, fk, issuedAt } = location.state || {};

  const { data, isFetching, isError } = useFetchSyncErrorDetails({
    companyId,
    employeeFk,
  });

  const title = [
    [firstName, lastName].filter(Boolean).join(" "),
    fk || employeeFk,
    issuedAt ? format(new Date(issuedAt), "dd.MM.yyyy, HH:mm:ss") : "",
  ]
    .filter(Boolean)
    .join(", ");

  const baseBreadcrumbs = (
    getCompanyBaseBreadcrumbs(companyId, companyName) as {
      title: string;
      link: string;
    }[]
  ).map(({ title, link }) => ({
    label: title,
    to: link,
  }));

  const breadcrumbs = isAhr
    ? [
        {
          label: `${__("Zarządzanie firmą")} ${companyName}`,
          to: `/ahr`,
        },
        {
          label: __("Błędy synchronizacji"),
          to: `/ahr/sync-errors`,
        },
        { label: title },
      ]
    : [
        ...baseBreadcrumbs,
        {
          label: __("Zarządzanie pracownikami"),
          to: `/company/edit/${companyId}/employee-management`,
        },
        {
          label: __("Błędy synchronizacji"),
          to: `/company/edit/${companyId}/employee-management/sync-errors`,
        },
        { label: title },
      ];

  const renderStatus = (error: string | null) =>
    error ? <StatusChip variant="error" /> : <StatusChip variant="success" />;

  return (
    <>
      <BreadcrumbsMui items={breadcrumbs} />
      <BackButton sx={{ my: 2 }} />
      <Typography variant="h4" fontWeight="fontWeightBold" mb={3}>
        {title}
      </Typography>
      <Alert severity="warning" sx={{ mb: 3 }}>
        <AlertTitle>{__("Żadne dane nie zostały zsynchronizowane")}</AlertTitle>
        {__("Popraw pola oznaczone jako błędne w systemie źródłowym")}
      </Alert>
      <TableContainer component={Paper}>
        <Table sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell>{__("Pole")}</TableCell>
              <TableCell>{__("Wartość")}</TableCell>
              <TableCell>{__("Status")}</TableCell>
              <TableCell>{__("Opis")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching && !data &&
              Array.from({ length: 14 }, (_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 4 }, (_, j) => (
                    <TableCell key={j}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {isError && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {__("Wystąpił błąd podczas pobierania danych")}
                </TableCell>
              </TableRow>
            )}
            {data?.map((row: SyncErrorDetail) => (
              <TableRow key={row.fieldName}>
                <TableCell>{row.fieldName}</TableCell>
                <TableCell>{row.fieldValue ?? "-"}</TableCell>
                <TableCell>{renderStatus(row.error)}</TableCell>
                <TableCell>{__(getSyncErrorMessage(row.error))}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default SyncErrorDetails;
